import { ReportBackendTypes } from '@ais-ui/websockets';
import React, { FC, SVGProps } from 'react';
import type { IconType } from '@ais-ui/ui-core';
import { SailboatOffIcon } from '@ais-ui/ui-core';
import { v4 as uuidv4 } from 'uuid';
import { COLOR_SCALES } from '../utils/colorScales';
import {
  PiAnchor,
  PiArrowsLeftRight,
  PiBoat,
  PiBroadcast,
  PiCamera,
  PiChartPolar,
  PiChartScatter,
  PiCompassTool,
  PiDatabase,
  PiDog,
  PiFish,
  PiFileText,
  PiGauge,
  PiHash,
  PiLifebuoy,
  PiLineSegments,
  PiMapTrifold,
  PiPath,
  PiPolygon,
  PiRobot,
} from 'react-icons/pi';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { DateRange } from 'react-day-picker';
import { SelectType } from '@ais-ui/ui-core';

export type FilterType = 'normal' | 'ship' | 'shiplist' | 'combined';
export type ShapeType = 'shape' | 'multiShape' | 'worldShape' | 'ports';

type enabledOn = {
  field: string;
  value: string | boolean;
};

export type MiscellaneousOptions = {
  label: string;
  value: any;
};

export type ColorInterval = {
  id: string;
  color: string;
  startInterval: number;
  endInterval: number;
};

export type VideoSegment = {
  id: string;
  dateRange: DateRange | undefined;
  shapeId: string | undefined;
  shape: GeoJson | undefined;
  videoSpeed: number;
  showLabels: boolean;
  showAllShips: boolean;
};

type Validation = {
  compareField?: string;
  compareValue?: number; // Used for list size validations
  operator: '<' | '>';
  message: string;
};

export type MiscellaneousField = {
  fieldType:
    | 'date'
    | 'select'
    | 'multiSelect'
    | 'multiSelectDualPanelDragNDrop'
    | 'multiSelectDualPanel'
    | 'string'
    | 'text'
    | 'number'
    | 'dayTimeDuration'
    | 'boolean'
    | 'timeDuration'
    | 'colorInterval'
    | 'videoSegments';
  defaultValue?: any;
  name: string;
  help?: string;
  placeholder: string;
  description?: string;
  required: boolean;
  options?: MiscellaneousOptions[]; // Used for 'select' and 'multiSelect' fieldType
  fetchOptions?: 'watchdog'; // Sometimes the options are dynamic to the user, in that case we need to specify where to fetch them
  enabledOn?: enabledOn;
  validation?: Validation;
  maxLength?: number;
  group?: string; // Group fields with the same group value on the same row
  order?: number; // Order fields within the form (lower numbers appear first)
};

export type PrefilledField = {
  fieldName: string;
  fieldValue: any;
};

export type PrefillObject = {
  name: string;
  fields: PrefilledField[];
};

export type PrefilledFieldsMap = {
  [fieldKey: string]: PrefillObject;
};

export type ReportShapeItem = {
  name: string;
  label: string;
  allowedGeometryTypes: (
    | 'Point'
    | 'LineString'
    | 'Polygon'
    | 'TwoPointLineString'
    | 'Circle'
  )[];
  backendKey: string;
  help?: string;
  minItems?: number;
  allowFeatureCollections?: boolean;
};

export type ShapeStructure = {
  shape?: ReportShapeItem[];
  multiShape?: ReportShapeItem[];
  worldShape?: ReportShapeItem[];
  ports?: ReportShapeItem[];
};

export type ReportFilterItem = {
  name: string;
  label: string;
  required: boolean;
  help?: string;
};

export type FilterStructure = {
  normal?: ReportFilterItem[];
  shiplist?: ReportFilterItem[];
  ship?: ReportFilterItem[];
  combined?: ReportFilterItem[]; // In case there needs to be both a normal and a shiplist/ship filter
};

export type ShipsStructure = {
  colorBy: boolean;
  filterBy: boolean;
};

export interface ReportType {
  label: string;
  description: string;
  type: ReportBackendTypes;
  icon: IconType | FC<SVGProps<SVGSVGElement>>;
  ships?: ShipsStructure | null | undefined;
  filter: FilterStructure | null;
  shape: ShapeStructure | null;
  miscellaneous: Record<string, MiscellaneousField>;
  prefilledFieldsMap?: PrefilledFieldsMap;
  disabled: boolean;
}

const CustomRotatedSquareIcon: React.FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="-50 -50 100 100" xmlns="http://www.w3.org/2000/svg" {...props}>
    <rect
      x="-35"
      y="-35"
      width="70"
      height="70"
      transform="rotate(45)"
      fill="none"
      stroke="currentColor"
      strokeWidth="4"
    />
  </svg>
);

export const sogDistributionReportMiscellaneous: Record<
  string,
  MiscellaneousField
> = {
  distribution: {
    fieldType: 'select',
    defaultValue: 'running',
    name: 'Statistic grouping',
    placeholder: 'Select statistic grouping',
    required: true,
    options: [
      {
        label: 'running',
        value: 'running',
      },
      {
        label: 'distribution',
        value: 'distribution',
      },
    ],
  },
  interval: {
    fieldType: 'select',
    defaultValue: 'year',
    name: 'Interval',
    placeholder: 'Select interval',
    required: true,
    help: 'Choose the time interval for grouping data.',
    options: [
      {
        label: 'years',
        value: 'year',
      },
      {
        label: 'quarters',
        value: 'quarter',
      },
      {
        label: 'months',
        value: 'month',
      },
      {
        label: 'weeks',
        value: 'week',
      },
      {
        label: 'days',
        value: 'day',
      },
    ],
  },
};

export const sqlReportMiscellaneous: Record<string, MiscellaneousField> = {
  query: {
    fieldType: 'text',
    defaultValue: '',
    name: 'Query',
    placeholder: 'Enter query',
    required: true,
  },
};

export const atonDetailedReportMiscellaneous: Record<
  string,
  MiscellaneousField
> = {
  mmsi: {
    fieldType: 'number',
    defaultValue: 0,
    name: 'MMSI',
    placeholder: 'Enter MMSI',
    required: true,
  },
};

const areaReportMiscellaneous: Record<string, MiscellaneousField> = {
  statisticGrouping: {
    fieldType: 'select',
    defaultValue: 'shiplist',
    name: 'Statistic grouping',
    placeholder: 'Select statistic grouping',
    required: true,
    help: 'Select an output of how the ships are sorted.',
    options: [
      {
        label: 'shiplist mmsi',
        value: 'shiplist',
      },
      {
        label: 'ungrouped',
        value: 'ungrouped',
      },
    ],
  },
  interval: {
    fieldType: 'select',
    defaultValue: 'year',
    name: 'Interval',
    placeholder: 'Select interval',
    required: true,
    help: 'Choose the time interval for grouping data.',
    options: [
      {
        label: 'years',
        value: 'year',
      },
      {
        label: 'quarters',
        value: 'quarter',
      },
      {
        label: 'months',
        value: 'month',
      },
      {
        label: 'weeks',
        value: 'week',
      },
      {
        label: 'days',
        value: 'day',
      },
    ],
    enabledOn: { field: 'statisticGrouping', value: 'shiplist' },
  },
};

const mmsiChangeReportMiscellaneous: Record<string, MiscellaneousField> = {};

export const coverageReportMiscellaneous: Record<string, MiscellaneousField> = {
  cellResolution: {
    fieldType: 'number',
    defaultValue: 0.05,
    name: 'Column division value (NM)',
    placeholder: 'Select cell resolution',
    required: true,
  },
  colorDivision: {
    fieldType: 'number',
    defaultValue: 40,
    name: 'Color division',
    placeholder: 'Select color division',
    required: true,
  },
  plotType: {
    fieldType: 'select',
    defaultValue: 'image',
    name: 'Plot type',
    placeholder: 'Select plot type',
    required: true,
    options: [
      {
        label: 'image',
        value: 'image',
      },
      {
        label: 'smoothed image',
        value: 'simage',
      },
      {
        label: 'contour',
        value: 'contour',
      },
    ],
  },
};

export const passagelineReportMiscellaneous: Record<
  string,
  MiscellaneousField
> = {
  outputType: {
    fieldType: 'select',
    defaultValue: 'count',
    name: 'Output type',
    placeholder: 'Select output type',
    required: true,
    help: 'The report will either output a count of ships crossing the line(s), specific ships crossing the line(s), or display a histogram of ships crossing the line(s).',
    options: [
      {
        label: 'count',
        value: 'count',
      },
      {
        label: 'ships',
        value: 'ships',
      },
      {
        label: 'histogram',
        value: 'histogram',
      },
    ],
  },
  statisticGrouping: {
    fieldType: 'select',
    defaultValue: 'ungrouped',
    name: 'Statistic grouping',
    placeholder: 'Select statistic grouping',
    required: true,
    help: 'Select an output of how the ships are sorted.',
    options: [
      {
        label: 'ungrouped',
        value: 'ungrouped',
      },
      {
        label: 'country',
        value: 'country',
      },
      {
        label: 'ship type',
        value: 'shiptype',
      },
    ],
    enabledOn: { field: 'outputType', value: 'count' },
  },
  interval: {
    fieldType: 'select',
    defaultValue: 'year',
    name: 'Interval',
    placeholder: 'Select interval',
    required: true,
    help: 'Choose the time interval for grouping data.',
    options: [
      {
        label: 'years',
        value: 'year',
      },
      {
        label: 'quarters',
        value: 'quarter',
      },
      {
        label: 'months',
        value: 'month',
      },
      {
        label: 'weeks',
        value: 'week',
      },
      {
        label: 'days',
        value: 'day',
      },
    ],
    enabledOn: { field: 'outputType', value: 'count' },
  },
  columnDivisionValue: {
    fieldType: 'number',
    defaultValue: 1,
    name: 'Column division value',
    placeholder: 'Select column division value',
    required: true,
    enabledOn: { field: 'outputType', value: 'histogram' },
  },
  columnDivisionUnit: {
    fieldType: 'select',
    defaultValue: 'NM',
    name: 'Column division unit',
    placeholder: 'Select column division',
    required: true,
    options: [
      {
        label: 'nautical miles',
        value: 'NM',
      },
      {
        label: 'meter',
        value: 'M',
      },
      {
        label: 'columns',
        value: 'COL',
      },
    ],
    enabledOn: { field: 'outputType', value: 'histogram' },
  },
  crossingConfig: {
    fieldType: 'boolean',
    defaultValue: false,
    name: 'Crossing configuration',
    placeholder: '',
    required: false,
  },
  minCrossingDistance: {
    fieldType: 'number',
    defaultValue: 0,
    help: 'If this is set, a ship will only be considered to have crossed the passage line once it has moved the specified distance beyond the passage line.',
    name: 'Min crossing distance',
    placeholder: 'Select min crossing distance',
    required: false,
    enabledOn: { field: 'crossingConfig', value: true },
  },
  minCrossingInterval: {
    fieldType: 'timeDuration',
    defaultValue: '00:00',
    help: 'If this is set, a crossing will not be counted if it occurs before the specified time has passed since the previous crossing of the ship.',
    name: 'Min crossing interval',
    placeholder: 'Select min crossing interval',
    required: false,
    enabledOn: { field: 'crossingConfig', value: true },
  },
};

export const passagelineShipsReportMiscellaneous: Record<
  string,
  MiscellaneousField
> = {
  outputType: {
    fieldType: 'select',
    defaultValue: 'count',
    name: 'Output type',
    placeholder: 'Select output type',
    required: true,
    help: 'The report will either output a count of ships crossing the line(s) or specific ships crossing the line(s).',
    options: [
      {
        label: 'count',
        value: 'count',
      },
      {
        label: 'ships',
        value: 'ships',
      },
    ],
  },
  statisticGrouping: {
    fieldType: 'select',
    defaultValue: 'ungrouped',
    name: 'Statistic grouping',
    placeholder: 'Select statistic grouping',
    required: true,
    help: 'Select an output of how the ships are sorted.',
    options: [
      {
        label: 'ungrouped',
        value: 'ungrouped',
      },
      {
        label: 'country',
        value: 'country',
      },
      {
        label: 'ship type',
        value: 'shiptype',
      },
    ],
    enabledOn: { field: 'outputType', value: 'count' },
  },
  interval: {
    fieldType: 'select',
    defaultValue: 'year',
    name: 'Interval',
    placeholder: 'Select interval',
    required: true,
    help: 'Choose the time interval for grouping data.',
    options: [
      {
        label: 'years',
        value: 'year',
      },
      {
        label: 'quarters',
        value: 'quarter',
      },
      {
        label: 'months',
        value: 'month',
      },
      {
        label: 'weeks',
        value: 'week',
      },
      {
        label: 'days',
        value: 'day',
      },
    ],
    enabledOn: { field: 'outputType', value: 'count' },
  },
  bothDirections: {
    fieldType: 'boolean',
    defaultValue: false,
    name: 'Both directions',
    placeholder: '',
    help: 'If this is enabled the ships can pass the lines in any order.',
    required: false,
  },
  maxDuration: {
    fieldType: 'dayTimeDuration',
    defaultValue: undefined,
    name: 'Max duration',
    placeholder: 'Select max duration',
    help: 'The maximum duration it will take for ships to pass the lines for them to be included in the report.',
    required: true,
  },
};

export const multiShapeReportMiscellaneous: Record<string, MiscellaneousField> =
  {
    outputType: {
      fieldType: 'select',
      defaultValue: 'count',
      name: 'Output type',
      placeholder: 'Select output type',
      required: true,
      options: [
        {
          label: 'count',
          value: 'count',
        },
        {
          label: 'ships',
          value: 'ships',
        },
      ],
    },
    statisticGrouping: {
      fieldType: 'select',
      defaultValue: 'ungrouped',
      name: 'Statistic grouping',
      placeholder: 'Select statistic grouping',
      required: true,
      help: 'Select an output of how the ships are sorted.',
      options: [
        {
          label: 'ungrouped',
          value: 'ungrouped',
        },
        {
          label: 'country',
          value: 'country',
        },
        {
          label: 'ship type',
          value: 'shiptype',
        },
      ],
      enabledOn: { field: 'outputType', value: 'count' },
    },
    interval: {
      fieldType: 'select',
      defaultValue: 'year',
      name: 'Interval',
      placeholder: 'Select interval',
      required: true,
      help: 'Choose the time interval for grouping data.',
      options: [
        {
          label: 'years',
          value: 'year',
        },
        {
          label: 'quarters',
          value: 'quarter',
        },
        {
          label: 'months',
          value: 'month',
        },
        {
          label: 'weeks',
          value: 'week',
        },
        {
          label: 'days',
          value: 'day',
        },
      ],
      enabledOn: { field: 'outputType', value: 'count' },
    },
    bothDirections: {
      fieldType: 'boolean',
      defaultValue: false,
      name: 'Both directions',
      placeholder: '',
      required: true,
    },
    maxDuration: {
      fieldType: 'dayTimeDuration',
      defaultValue: undefined,
      name: 'Max duration',
      placeholder: 'Select max duration',
      required: true,
    },
  };

const transponderOffReportMiscellaneous: Record<string, MiscellaneousField> = {
  maxDuration: {
    fieldType: 'dayTimeDuration',
    defaultValue: undefined,
    name: 'Max duration',
    placeholder: 'Select max duration',
    required: true,
  },
};

const fishingReportMiscellaneous: Record<string, MiscellaneousField> = {
  minDuration: {
    fieldType: 'dayTimeDuration',
    defaultValue: undefined,
    name: 'Min duration',
    placeholder: 'Select min duration',
    required: true,
  },
};

export const densityReportMiscellaneous: Record<string, MiscellaneousField> = {
  colorScale: {
    fieldType: 'select',
    defaultValue: 'yellow to red',
    name: 'Color scale',
    placeholder: 'Select color scale',
    options: COLOR_SCALES.map((scale) => ({
      label: scale.label,
      value: scale.value,
    })),
    required: true,
  },
  colorBy: {
    fieldType: 'select',
    defaultValue: 'DENSITY-NUM-SHIPS-GRAPH',
    name: 'Color by',
    placeholder: 'Select color by',
    options: [
      {
        label: 'number of ships',
        value: 'DENSITY-NUM-SHIPS-GRAPH',
      },
      {
        label: 'distinct ships',
        value: 'DENSITY-DISTINCT-SHIPS-GRAPH',
      },
      {
        label: 'average SOG',
        value: 'DENSITY-AVG-SOG-GRAPH',
      },
      {
        label: 'draught',
        value: 'DENSITY-DRAUGHT-GRAPH',
      },
    ],
    required: true,
  },
  cellsPerDegree: {
    fieldType: 'select',
    defaultValue: '100',
    name: 'Cells per degree',
    placeholder: 'Select cells per degree',
    help: 'Increase cell count to view more detail on a heatmap. High cell count = long load times.',
    options: [
      {
        label: '10',
        value: '10',
      },
      {
        label: '100',
        value: '100',
      },
      {
        label: '1000',
        value: '1000',
      },
      {
        label: '10000',
        value: '10000',
      },
    ],
    required: true,
  },
  colorDivision: {
    fieldType: 'number',
    defaultValue: 40,
    name: 'Color division',
    placeholder: 'Select color division',
    help: 'The number of different color intervals the data is colored by (optional).',
    required: true,
  },
};

export const trackReportMiscellaneous: Record<string, MiscellaneousField> = {
  colorBy: {
    fieldType: 'select',
    defaultValue: 'cog',
    name: 'Color by',
    placeholder: 'Choose what to color tracks by',
    options: [
      {
        label: 'Course over ground',
        value: 'cog',
      },
      {
        label: 'Speed over ground',
        value: 'sog',
      },
      {
        label: 'Draught',
        value: 'draught',
      },
      {
        label: 'Length',
        value: 'length',
      },
      {
        label: 'Width',
        value: 'volume',
      },
      {
        label: 'Volume',
        value: 'volume',
      },
    ],
    required: true,
  },
  colorByInterval: {
    fieldType: 'colorInterval',
    defaultValue: [
      { id: uuidv4(), color: '#3ea908', startInterval: 0, endInterval: 180 },
      { id: uuidv4(), color: '#e50000', startInterval: 180, endInterval: 360 },
    ],
    required: true,
    name: 'Color interval',
    help: 'Enter hex color value or click and choose to color the tracks.',
    placeholder: '',
  },
};

const tripsReportMiscellaneous: Record<string, MiscellaneousField> = {
  maxDuration: {
    fieldType: 'dayTimeDuration',
    defaultValue: undefined,
    name: 'Max duration',
    help: 'Filter trips by maximum trip duration within the specified period.',
    required: true,
    placeholder: 'Choose max duration',
  },
  excludeLocalOperations: {
    fieldType: 'boolean',
    defaultValue: false,
    required: true,
    name: 'Exclude local operations',
    help: "If the two areas are overlapping then 'Exclude local operations' can be set to ignore pilot and tug boats etc. that are only operating out of one port.",
    placeholder: '',
  },
};

const shipToShipReportPrefill: PrefilledFieldsMap = {
  pilotOperations: {
    name: 'Pilot operations',
    fields: [
      {
        fieldName: 'minDeltaCourseOverGround',
        fieldValue: 0,
      },
      {
        fieldName: 'maxDeltaCourseOverGround',
        fieldValue: 30,
      },
      {
        fieldName: 'minDeltaSpeedOverGround',
        fieldValue: 0,
      },
      {
        fieldName: 'maxDeltaSpeedOverGround',
        fieldValue: 3,
      },
      {
        fieldName: 'minSpeedOverGround',
        fieldValue: 2,
      },
      {
        fieldName: 'maxSpeedOverGround',
        fieldValue: 10,
      },
      {
        fieldName: 'minDuration',
        fieldValue: '',
      },
      {
        fieldName: 'maxDuration',
        fieldValue: '',
      },
      {
        fieldName: 'minTravelDistance',
        fieldValue: '',
      },
      {
        fieldName: 'maxTravelDistance',
        fieldValue: '',
      },
    ],
  },
  collision: {
    name: 'Collision detection',
    fields: [
      {
        fieldName: 'minDeltaCourseOverGround',
        fieldValue: 45,
      },
      {
        fieldName: 'maxDeltaCourseOverGround',
        fieldValue: '',
      },
      {
        fieldName: 'minDeltaSpeedOverGround',
        fieldValue: 5,
      },
      {
        fieldName: 'maxDeltaSpeedOverGround',
        fieldValue: '',
      },
      {
        fieldName: 'minSpeedOverGround',
        fieldValue: '',
      },
      {
        fieldName: 'maxSpeedOverGround',
        fieldValue: '',
      },
      {
        fieldName: 'minDuration',
        fieldValue: '',
      },
      {
        fieldName: 'maxDuration',
        fieldValue: '',
      },
      {
        fieldName: 'minTravelDistance',
        fieldValue: 0,
      },
      {
        fieldName: 'maxTravelDistance',
        fieldValue: '',
      },
    ],
  },
  tugOperations: {
    name: 'Tug operations',
    fields: [
      {
        fieldName: 'minDeltaCourseOverGround',
        fieldValue: '',
      },
      {
        fieldName: 'maxDeltaCourseOverGround',
        fieldValue: '',
      },
      {
        fieldName: 'minDeltaSpeedOverGround',
        fieldValue: '',
      },
      {
        fieldName: 'maxDeltaSpeedOverGround',
        fieldValue: '',
      },
      {
        fieldName: 'minSpeedOverGround',
        fieldValue: '',
      },
      {
        fieldName: 'maxSpeedOverGround',
        fieldValue: '',
      },
      {
        fieldName: 'minDuration',
        fieldValue: 300,
      },
      {
        fieldName: 'maxDuration',
        fieldValue: '',
      },
      {
        fieldName: 'minTravelDistance',
        fieldValue: 0.5,
      },
      {
        fieldName: 'maxTravelDistance',
        fieldValue: '',
      },
    ],
  },
};

const shipToShipReportMiscellaneous: Record<string, MiscellaneousField> = {
  maxClosestPointOfApproachDistance: {
    fieldType: 'number',
    defaultValue: 300,
    required: true,
    name: 'Max closest point of approach distance (m)',
    placeholder: 'Choose max CPA distance',
  },
  minDeltaCourseOverGround: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Min delta of course over ground (degrees)',
    placeholder: 'Choose between 0 and 360',
    validation: {
      compareField: 'maxDeltaCourseOverGround',
      operator: '<',
      message: 'Min delta COG must be smaller than max delta COG',
    },
  },
  maxDeltaCourseOverGround: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Max delta of course over ground (degrees)',
    placeholder: 'Choose between 0 and 360',
    validation: {
      compareField: 'minDeltaCourseOverGround',
      operator: '>',
      message: 'Max delta COG must be larger than min delta COG',
    },
  },
  minDeltaSpeedOverGround: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Min delta of speed over ground (Kn)',
    placeholder: 'Choose min delta of speed over ground',
    validation: {
      compareField: 'maxDeltaSpeedOverGround',
      operator: '<',
      message: 'Min delta SOG must be smaller than max delta SOG',
    },
  },
  maxDeltaSpeedOverGround: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Max delta of speed over ground (Kn)',
    placeholder: 'Choose max delta of speed over ground',
    validation: {
      compareField: 'minDeltaSpeedOverGround',
      operator: '>',
      message: 'Max delta SOG must be larger than min delta SOG',
    },
  },
  minSpeedOverGround: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Min of speed over ground (Kn)',
    placeholder: 'Choose min of speed over ground',
    validation: {
      compareField: 'maxSpeedOverGround',
      operator: '<',
      message: 'Min SOG must be smaller than max SOG',
    },
  },
  maxSpeedOverGround: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Max of speed over ground (Kn)',
    placeholder: 'Choose max of speed over ground',
    validation: {
      compareField: 'minSpeedOverGround',
      operator: '>',
      message: 'Max SOG must be larger than min SOG',
    },
  },
  minDuration: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Min duration (s)',
    placeholder: 'Choose min duration',
    validation: {
      compareField: 'maxDuration',
      operator: '<',
      message: 'Min duration must be smaller than max duration',
    },
  },
  maxDuration: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Max duration (s)',
    placeholder: 'Choose max duration',
    validation: {
      compareField: 'minDuration',
      operator: '>',
      message: 'Max duration must be larger than min duration',
    },
  },
  minTravelDistance: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Min travel distance (Nm)',
    placeholder: 'Choose min travel distance',
    validation: {
      compareField: 'maxTravelDistance',
      operator: '<',
      message: 'Min travel distance must be smaller than max travel distance',
    },
  },
  maxTravelDistance: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Max travel distance (Nm)',
    placeholder: 'Choose max travel distance',
    validation: {
      compareField: 'minTravelDistance',
      operator: '>',
      message: 'Max travel distance must be larger than min travel distance',
    },
  },
};

const pilotReportMiscellaneous: Record<string, MiscellaneousField> = {
  maxClosestPointOfApproachDistance: {
    fieldType: 'number',
    defaultValue: 300,
    required: true,
    name: 'Max closest point of approach distance (m)',
    placeholder: 'Choose max CPA distance',
  },
  minDeltaCourseOverGround: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Min delta of course over ground (degrees)',
    placeholder: 'Choose between 0 and 360',
    validation: {
      compareField: 'maxDeltaCourseOverGround',
      operator: '<',
      message: 'Min delta COG must be smaller than max delta COG',
    },
  },
  maxDeltaCourseOverGround: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Max delta of course over ground (degrees)',
    placeholder: 'Choose between 0 and 360',
    validation: {
      compareField: 'minDeltaCourseOverGround',
      operator: '>',
      message: 'Max delta COG must be larger than min delta COG',
    },
  },
  minDeltaSpeedOverGround: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Min delta of speed over ground (Kn)',
    placeholder: 'Choose min delta of speed over ground',
    validation: {
      compareField: 'maxDeltaSpeedOverGround',
      operator: '<',
      message: 'Min delta SOG must be smaller than max delta SOG',
    },
  },
  maxDeltaSpeedOverGround: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Max delta of speed over ground (Kn)',
    placeholder: 'Choose max delta of speed over ground',
    validation: {
      compareField: 'minDeltaSpeedOverGround',
      operator: '>',
      message: 'Max delta SOG must be larger than min delta SOG',
    },
  },
  minSpeedOverGround: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Min speed over ground (Kn)',
    placeholder: 'Choose min speed over ground',
    validation: {
      compareField: 'maxSpeedOverGround',
      operator: '<',
      message: 'Min SOG must be smaller than max SOG',
    },
  },
  maxSpeedOverGround: {
    fieldType: 'number',
    defaultValue: undefined,
    required: false,
    name: 'Max speed over ground (Kn)',
    placeholder: 'Choose max speed over ground',
    validation: {
      compareField: 'minSpeedOverGround',
      operator: '>',
      message: 'Max SOG must be larger than min SOG',
    },
  },
};

const shipToPointReportMiscellaneous: Record<string, MiscellaneousField> = {
  distanceThreshold: {
    fieldType: 'number',
    defaultValue: 250,
    required: true,
    name: 'Distance threshold (m)',
    help: 'Maximum distance between ship to point, that should be included in the report in meters.',
    placeholder: 'Choose distance threshold',
  },
};
const replayVideoReportMiscellaneous: Record<string, MiscellaneousField> = {
  videoTitle: {
    fieldType: 'string',
    defaultValue: '',
    name: 'Video title',
    placeholder: 'Enter video title',
    required: false,
    maxLength: 50,
    order: 1,
  },
  videoSpeed: {
    fieldType: 'select',
    defaultValue: '1',
    name: 'Video speed',
    placeholder: 'Choose video speed',
    options: [
      {
        label: '1x',
        value: '1',
      },
      {
        label: '2x',
        value: '2',
      },
      {
        label: '4x',
        value: '4',
      },
      {
        label: '8x',
        value: '8',
      },
      {
        label: '16x',
        value: '16',
      },
      {
        value: '32',
        label: '32x',
      },
      {
        label: '64x',
        value: '64',
      },
      {
        label: '128x',
        value: '128',
      },
    ],
    required: true,
    order: 2,
    group: 'videoSpeedResolution',
  },
  videoResolution: {
    fieldType: 'select',
    defaultValue: '640x480',
    name: 'Video resolution',
    placeholder: 'Choose video resolution',
    options: [
      {
        label: '640 x 480',
        value: '640x480',
      },
      {
        label: '1024 x 768',
        value: '1024x768',
      },
      {
        label: '1280 x 1024',
        value: '1280x1024',
      },
      {
        label: '1600 x 900',
        value: '1600x900',
      },
      {
        label: '1600 x 1200',
        value: '1600x1200',
      },
      {
        label: '1920 x 720',
        value: '1920x720',
      },
      {
        label: '1920 x 1080',
        value: '1920x1080',
      },
      {
        label: '2048 x 1536',
        value: '2048x1536',
      },
      {
        label: '2048 x 2048',
        value: '2048x2048',
      },
      {
        label: '2560 x 1440',
        value: '2560x1440',
      },
      {
        label: '2560 x 1600',
        value: '2560x1600',
      },
      {
        label: '3840 x 2160',
        value: '3840x2160',
      },
      {
        label: '4096 x 3072',
        value: '4096x3072',
      },
      {
        label: '4096 x 4096',
        value: '4096x4096',
      },
    ],
    required: true,
    order: 2,
    group: 'videoSpeedResolution',
  },
  videoFormat: {
    fieldType: 'select',
    defaultValue: 'mp4',
    name: 'Video format',
    placeholder: 'Choose video format',
    options: [
      {
        label: 'MP4',
        value: 'mp4',
      },
      {
        label: 'WMV',
        value: 'wmv',
      },
    ],
    required: true,
    order: 3,
    group: 'videoFormatQuality',
  },
  videoQuality: {
    fieldType: 'select',
    defaultValue: 'low',
    name: 'Video quality',
    placeholder: 'Choose video quality',
    options: [
      {
        label: 'Low',
        value: 'low',
      },
      {
        label: 'Medium',
        value: 'medium',
      },
      {
        label: 'High',
        value: 'high',
      },
      {
        label: 'High (raw data)',
        value: 'highraw',
      },
    ],
    required: true,
    order: 3,
    group: 'videoFormatQuality',
  },
  mapType: {
    fieldType: 'select',
    defaultValue: 'osm',
    name: 'Map type',
    placeholder: 'Choose map type',
    options: [
      {
        label: 'OSM',
        value: 'osm',
      },
      {
        label: 'S-57',
        value: 's-57',
      },
    ],
    required: true,
    order: 4,
  },
  showTracks: {
    fieldType: 'boolean',
    defaultValue: false,
    required: true,
    name: 'Show tracks',
    placeholder: '',
    help: 'Show tracks for selected vessels',
    order: 5,
  },
  videoSegments: {
    fieldType: 'videoSegments',
    defaultValue: [],
    required: false,
    name: 'Video segments',
    placeholder: '',
    help: 'Add segments to the video, where it speeds up, slows down or zooms to a specific area.',
    order: 6,
  },
};

export const csvDataExportReportMiscellaneous: Record<
  string,
  MiscellaneousField
> = {
  dataFields: {
    fieldType: 'multiSelectDualPanelDragNDrop',
    defaultValue: [],
    name: 'Data fields',
    placeholder: 'Select data fields to be exported',
    validation: {
      compareValue: 0,
      operator: '>',
      message: 'Choose at least one data field',
    },
    options: [
      {
        label: 'AIS message ID',
        value: 'SENTENCE-TYPE',
      },
      {
        label: 'Base station timestamp',
        value: 'BS-TS',
      },
      {
        label: 'Callsign',
        value: 'CALLSIGN',
      },
      {
        label: 'Communication state',
        value: 'COMM-STATE',
      },
      {
        label: 'Country (AIS)',
        value: 'COUNTRY-AIS',
      },
      {
        label: 'Course over Ground (COG)',
        value: 'COG',
      },
      {
        label: 'Data source MMSI',
        value: 'DS-MMSI',
      },
      {
        label: 'Data source country',
        value: 'DS-COUNTRY',
      },
      {
        label: 'Data source region',
        value: 'DS-REGION',
      },

      {
        label: 'Data source type',
        value: 'DATASOURCE-TYPE',
      },
      {
        label: 'Data source type (text)',
        value: 'DATASOURCE-TYPE-TEXT',
      },
      {
        label: 'Destination',
        value: 'DESTINATION',
      },
      {
        label: 'Draught',
        value: 'DRAUGHT',
      },
      {
        label: 'Estimated time of arrival day',
        value: 'ETA-DAY',
      },
      {
        label: 'Estimated time of arrival hour',
        value: 'ETA-HOUR',
      },
      {
        label: 'Estimated time of arrival minute',
        value: 'ETA-MIN',
      },
      {
        label: 'Estimated time of arrival month',
        value: 'ETA-MON',
      },
      {
        label: 'Heading',
        value: 'HEADING',
      },

      {
        label: 'IMO',
        value: 'IMO',
      },
      {
        label: 'Latitude',
        value: 'LAT',
      },
      {
        label: 'Longitude',
        value: 'LON',
      },
      {
        label: 'MMSI',
        value: 'MMSI',
      },
      {
        label: 'NMEA message',
        value: 'NMEA-MESSAGE',
      },
      {
        label: 'NMEA sentence type',
        value: 'NMEA-TYPE',
      },
      {
        label: 'Navigational status',
        value: 'NAV-STATUS',
      },
      {
        label: 'Navigational status (text)',
        value: 'NAV-STATUS-TEXT',
      },

      {
        label: 'PGHP1 timestamp',
        value: 'TS-PGHP',
      },
      {
        label: 'Position accuracy',
        value: 'POS-ACCURACY',
      },
      {
        label: 'RAIM flag',
        value: 'RAIM',
      },
      {
        label: 'Repeat indicator',
        value: 'REPEAT-INDICATOR',
      },
      {
        label: 'Rotation',
        value: 'ROT',
      },

      {
        label: 'Ship name',
        value: 'NAME',
      },
      {
        label: 'Size A',
        value: 'SIZE-A',
      },
      {
        label: 'Size B',
        value: 'SIZE-B',
      },
      {
        label: 'Size C',
        value: 'SIZE-C',
      },
      {
        label: 'Size D',
        value: 'SIZE-D',
      },
      {
        label: 'Special manoeuvre indicator',
        value: 'REGIONAL-APPS',
      },
      {
        label: 'Speed over ground',
        value: 'SOG',
      },
      {
        label: 'TAG timestamp',
        value: 'TS-TAG',
      },

      {
        label: 'Target class',
        value: 'TARGET-CLASS',
      },
      {
        label: 'Target class (text)',
        value: 'TARGET-CLASS-TEXT',
      },
      {
        label: 'Type of cargo',
        value: 'TYPE-OF-CARGO',
      },
      {
        label: 'Type of cargo (text)',
        value: 'TYPE-OF-CARGO-TEXT',
      },
      {
        label: 'Type of position fixing device',
        value: 'TYPE-OF-POS-FIX-DEV',
      },
      {
        label: 'Type of ship',
        value: 'TYPE-OF-SHIP',
      },

      {
        label: 'Type of ship (text)',
        value: 'TYPE-OF-SHIP-TEXT',
      },
      {
        label: 'Type of ship and cargo',
        value: 'TYPE-OF-SHIP-AND-CARGO',
      },
      {
        label: 'UNIX timestamp',
        value: 'TS-UNIX',
      },
    ],
    required: true,
  },
  messageType: {
    fieldType: 'multiSelectDualPanel',
    defaultValue: [],
    help: 'Only data from these messages.',
    name: 'Message type',
    placeholder: 'Select message type',
    required: true,
    options: [
      {
        label: '(01) Position report',
        value: 'POSREP1',
      },
      {
        label: '(02) Position report',
        value: 'POSREP2',
      },
      {
        label: '(03) Position report',
        value: 'POSREP3',
      },
      {
        label: '(04) Base station report',
        value: 'BSREP',
      },
      {
        label: '(05) Static and voyage related data',
        value: 'STATVOY',
      },
      {
        label: '(06) Binary addressed message',
        value: 'BINADDR',
      },
      {
        label: '(07) Binary acknowledgement',
        value: 'BINACK',
      },
      {
        label: '(08) Binary broadcast message',
        value: 'BINBROAD',
      },
      {
        label: '(09) Standard SAR aircraft position',
        value: 'SARPOSREP',
      },
      {
        label: '(10) UTC(date inquiry',
        value: 'UTCINQ',
      },
      {
        label: '(11) UTC/date response',
        value: 'UTCRESP',
      },
      {
        label: '(12) Addressed safety related message',
        value: 'SFTYADDR',
      },
      {
        label: '(13) Safety related acknowledgement message',
        value: 'SFTYADDRACK',
      },
      {
        label: '(14) Safety related broadcast message',
        value: 'SFTYBROAD',
      },
      {
        label: '(15) Interrogation',
        value: 'INT',
      },
      {
        label: '(16) Assignment mode command',
        value: 'ASNGMODE',
      },
      {
        label: '(17) DGNSS broadcast binary message',
        value: 'DGNSS',
      },
      {
        label: '(18) Standard class B equipment position report',
        value: 'CLASSBSTD',
      },
      {
        label: '(19) Extended class B equipment position report',
        value: 'CLASSBEXT',
      },
      {
        label: '(20) Datalink management message',
        value: 'DATALINK',
      },
      {
        label: '(21) Aids-to-navigation report',
        value: 'ATONREP',
      },
      {
        label: '(22) Channel management',
        value: 'CHANMGNT',
      },
      {
        label: '(23) Group assignment command',
        value: 'GROUPASSGN',
      },
      {
        label: '(24) Class B static data report',
        value: 'CLASSBSTAT',
      },
      {
        label: '(25) Single slot binary',
        value: 'SSBIN',
      },
      {
        label: '(26) Multislot binary',
        value: 'MSBIN',
      },
      {
        label: '(27) Long range broadcast',
        value: 'LRBROAD',
      },
    ],
  },
  fewFiles: {
    fieldType: 'boolean',
    defaultValue: false,
    name: 'Generate few files',
    placeholder: '',
    required: false,
  },
};

export const csvDataExportFastReportMiscellaneous: Record<
  string,
  MiscellaneousField
> = {
  dataFields: {
    fieldType: 'multiSelectDualPanelDragNDrop',
    defaultValue: [],
    name: 'Data fields',
    placeholder: 'Select data fields to be exported',
    validation: {
      compareValue: 0,
      operator: '>',
      message: 'Choose at least one data field',
    },
    options: [
      {
        label: 'Base station timestamp',
        value: 'bs_ts',
      },
      {
        label: 'Callsign',
        value: 'callsign',
      },
      {
        label: 'Course over Ground (COG)',
        value: 'cog',
      },
      {
        label: 'Destination',
        value: 'destination',
      },
      {
        label: 'Draught',
        value: 'draught',
      },
      {
        label: 'ETA',
        value: 'eta',
      },
      {
        label: 'IMO',
        value: 'imo',
      },
      {
        label: 'Latitude',
        value: 'lat',
      },
      {
        label: 'Length',
        value: 'length',
      },
      {
        label: 'Longitude',
        value: 'lon',
      },
      {
        label: 'MMSI',
        value: 'mmsi',
      },
      {
        label: 'Navigational status (text)',
        value: 'nav_status',
      },
      {
        label: 'Ship name',
        value: 'name',
      },
      {
        label: 'Speed over ground',
        value: 'sog',
      },
      {
        label: 'Type of ship (text)',
        value: 'shiptype',
      },
      {
        label: 'Width',
        value: 'width',
      },
    ],
    required: true,
  },
  fewFiles: {
    fieldType: 'boolean',
    defaultValue: false,
    name: 'Generate few files',
    placeholder: '',
    required: false,
  },
};

const watchdogReportMiscellaneous: Record<string, MiscellaneousField> = {
  watchdogDefinitions: {
    fieldType: 'multiSelect',
    defaultValue: [],
    name: 'Watchdog and detector definitions',
    placeholder: 'Select watchdog/detector definitions',
    validation: {
      compareValue: 0,
      operator: '>',
      message: 'Choose at least one watchdog',
    },
    options: [],
    fetchOptions: 'watchdog',
    required: true,
  },
};

const transitPilotageReportMiscellaneous: Record<string, MiscellaneousField> = {
  maxClosestPointOfApproachDistance: {
    fieldType: 'number',
    defaultValue: 300,
    required: true,
    name: 'Max closest point of approach distance (m)',
    placeholder: 'Choose max CPA distance',
  },
  minDeltaCourseOverGround: {
    fieldType: 'number',
    defaultValue: 0,
    required: true,
    name: 'Min delta of course over ground (degrees)',
    placeholder: 'Choose between 0 and 360',
    help: 'The minimum change in degrees over a course.',
    validation: {
      compareField: 'maxDeltaCourseOverGround',
      operator: '<',
      message: 'Min delta COG must be smaller than max delta COG',
    },
  },
  maxDeltaCourseOverGround: {
    fieldType: 'number',
    defaultValue: 30,
    required: true,
    name: 'Max delta of course over ground (degrees)',
    placeholder: 'Choose between 0 and 360',
    help: 'The maximum change in degrees over a course.',
    validation: {
      compareField: 'minDeltaCourseOverGround',
      operator: '>',
      message: 'Max delta COG must be larger than min delta COG',
    },
  },
  minDeltaSpeedOverGround: {
    fieldType: 'number',
    defaultValue: 0,
    required: true,
    name: 'Min delta of speed over ground (Kn)',
    placeholder: 'Choose min delta of speed over ground',
    help: 'The minimum change in speed in knots over ground.',
    validation: {
      compareField: 'maxDeltaSpeedOverGround',
      operator: '<',
      message: 'Min delta SOG must be smaller than max delta SOG',
    },
  },
  maxDeltaSpeedOverGround: {
    fieldType: 'number',
    defaultValue: 3,
    required: true,
    name: 'Max delta of speed over ground (Kn)',
    placeholder: 'Choose max delta of speed over ground',
    help: 'The maximum change in speed in knots over ground.',
    validation: {
      compareField: 'minDeltaSpeedOverGround',
      operator: '>',
      message: 'Max delta SOG must be larger than min delta SOG',
    },
  },
  minSpeedOverGround: {
    fieldType: 'number',
    defaultValue: 0,
    required: true,
    name: 'Min speed over ground (Kn)',
    placeholder: 'Choose min speed over ground',
    validation: {
      compareField: 'maxSpeedOverGround',
      operator: '<',
      message: 'Min SOG must be smaller than max SOG',
    },
  },
  maxSpeedOverGround: {
    fieldType: 'number',
    defaultValue: 14,
    required: true,
    name: 'Max speed over ground (Kn)',
    placeholder: 'Choose max speed over ground',
    validation: {
      compareField: 'minSpeedOverGround',
      operator: '>',
      message: 'Max SOG must be larger than min SOG',
    },
  },
  maxDuration: {
    fieldType: 'dayTimeDuration',
    defaultValue: undefined,
    name: 'Max duration',
    placeholder: 'Select max duration',
    required: true,
  },
};

export function getSelectLabelByValue(
  value: string,
  options: SelectType[],
): string | undefined {
  const option = options.find((opt) => opt.value === value);
  return option ? option.label : undefined;
}

export const reportTypeToIcon: Record<
  string,
  IconType | React.FC<React.SVGProps<SVGSVGElement>>
> = {
  AREA_STAT_TB: PiMapTrifold,
  MMSI_CHANGE: PiHash,
  ATON_DETAILED_REPORT: CustomRotatedSquareIcon,
  ATON_OVERVIEW_REPORT: CustomRotatedSquareIcon,
  CSV_DATA_EXPORT: PiFileText,
  DATA_EXPORT_TB: PiFileText,
  COVERAGE: PiChartPolar,
  DENSITY_TB: PiChartScatter,
  FERRY_ROUTE: PiBoat,
  FISHING: PiFish,
  HISTORY_TRACK_TB: PiLineSegments,
  HISTORICAL_TRIPS: PiPath,
  MULTI_AREA: PiPolygon,
  NMSP2_TB: PiLifebuoy,
  NEARMISSSHIPTOSHIP_TB: PiLifebuoy,
  PSSSTATUS_DETAILED_REPORT: PiBroadcast,
  PSSSTATUS_OVERVIEW_REPORT: PiBroadcast,
  PASSAGELINE_TB: PiArrowsLeftRight,
  DATA_PASSAGELINE_MULTI: PiArrowsLeftRight,
  PILOTBOAT_TRIPS: PiRobot,
  PORT_CALLS: PiAnchor,
  REPLAY_VIDEO: PiCamera,
  SOG_DISTRIBUTION: PiGauge,
  GENERIC_SQL: PiDatabase,
  SHIPS_REPORT_TB: PiBoat,
  TRANSIT_PILOTAGE: PiCompassTool,
  TRANSPONDER_OFF: SailboatOffIcon,
  TUGBOAT_OPERATIONS: PiAnchor,
  WDEVENT: PiDog,
};

export const reportTypeToLabel: Record<string, string> = {
  AREA_STAT_TB: 'Area',
  MMSI_CHANGE: 'MMSI change',
  ATON_DETAILED_REPORT: 'AtoN detailed',
  ATON_OVERVIEW_REPORT: 'AtoN overview',
  CSV_DATA_EXPORT: 'CSV data export',
  DATA_EXPORT_TB: 'CSV Track Data Export',
  COVERAGE: 'Coverage',
  DENSITY_TB: 'Density',
  FERRY_ROUTE: 'Ferry route',
  FISHING: 'Fishing',
  HISTORY_TRACK_TB: 'Track',
  MULTI_AREA: 'Multi shape',
  NMSP2_TB: 'Near miss ship to point',
  NEARMISSSHIPTOSHIP_TB: 'Near miss ship to ship',
  PSSSTATUS_DETAILED_REPORT: 'PSS detailed',
  PSSSTATUS_OVERVIEW_REPORT: 'PSS overview',
  PASSAGELINE_TB: 'Passageline',
  DATA_PASSAGELINE_MULTI: 'Passageline ships',
  PILOTBOAT_TRIPS: 'Pilot operations',
  PORT_CALLS: 'Port calls',
  HISTORICAL_TRIPS: 'Port trips',
  REPLAY_VIDEO: 'Replay video',
  SOG_DISTRIBUTION: 'SOG distribution',
  GENERIC_SQL: 'SQL',
  SHIPS_REPORT_TB: 'Ships',
  TRANSIT_PILOTAGE: 'Transit pilotage',
  TRANSPONDER_OFF: 'Transponder off',
  TUGBOAT_OPERATIONS: 'Tugboat operations',
  WDEVENT: 'Watchdog events',
};

// Get available report types (excluding REPLAY_VIDEO)
export const getAvailableReportTypes = () => {
  return Object.values(ReportBackendTypes).filter(
    (type) => type !== ReportBackendTypes.REPLAY_VIDEO,
  );
};

// Get label for a report type using your existing mapping
export const getReportTypeLabel = (type: string): string => {
  return reportTypeToLabel[type] || type;
};

export const getReportTypeOptions = () => {
  return getAvailableReportTypes()
    .map((type) => ({
      value: type,
      label: getReportTypeLabel(type),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const reportTypes: ReportType[] = [
  {
    label: reportTypeToLabel[ReportBackendTypes.AREA],
    description:
      'Get statistical information of which vessels have entered and exited a defined area, and how many times they have done so. The report also includes the distances sailed by each vessel within the selected time.',
    type: ReportBackendTypes.AREA,
    icon: reportTypeToIcon[ReportBackendTypes.AREA],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: areaReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.ATON_DETAILED],
    description: 'Get detailed AtoN information',
    type: ReportBackendTypes.ATON_DETAILED,
    icon: reportTypeToIcon[ReportBackendTypes.ATON_DETAILED],
    filter: null,
    shape: null,
    miscellaneous: atonDetailedReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.ATON_OVERVIEW],
    description: 'Get an AtoN overview',
    type: ReportBackendTypes.ATON_OVERVIEW,
    icon: reportTypeToIcon[ReportBackendTypes.ATON_OVERVIEW],
    filter: null,
    shape: null,
    miscellaneous: {},
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.CSV_DATA_EXPORT],
    description: 'Export report data to a csv file',
    type: ReportBackendTypes.CSV_DATA_EXPORT,
    icon: reportTypeToIcon[ReportBackendTypes.CSV_DATA_EXPORT],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: csvDataExportReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.CSV_DATA_EXPORT_FAST],
    description: 'Export track (processed) vessel data to a csv file',
    type: ReportBackendTypes.CSV_DATA_EXPORT_FAST,
    icon: reportTypeToIcon[ReportBackendTypes.CSV_DATA_EXPORT_FAST],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon', 'LineString', 'Point', 'Circle'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: csvDataExportFastReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.COVERAGE],
    description: 'Get an ais-signal coverage report',
    type: ReportBackendTypes.COVERAGE,
    icon: reportTypeToIcon[ReportBackendTypes.COVERAGE],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: coverageReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.DENSITY],
    description:
      'Get density map on vessel movements within an area. Density can be colored by either numbers of ships, distinct ships, draught or avg. SOG.',
    type: ReportBackendTypes.DENSITY,
    icon: reportTypeToIcon[ReportBackendTypes.DENSITY],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
          allowFeatureCollections: true,
        },
      ],
    },
    miscellaneous: densityReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.FERRY_ROUTE],
    description: 'Get a ferry route report',
    type: ReportBackendTypes.FERRY_ROUTE,
    icon: reportTypeToIcon[ReportBackendTypes.FERRY_ROUTE],
    filter: {
      shiplist: [
        {
          name: 'filter',
          label: 'Filter by shiplist',
          required: true,
        },
      ],
    },
    shape: null,
    miscellaneous: {},
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.FISHING],
    description: 'Determine fishing ships',
    type: ReportBackendTypes.FISHING,
    icon: reportTypeToIcon[ReportBackendTypes.FISHING],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: fishingReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.MMSI_CHANGE],
    description:
      'Find vessels that have changed their MMSI within a defined area and time period.',
    type: ReportBackendTypes.MMSI_CHANGE,
    icon: reportTypeToIcon[ReportBackendTypes.MMSI_CHANGE],
    filter: null,
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: mmsiChangeReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.TRACK],
    description:
      'Get map/graphs displaying AIS tracks within a specific time and area. The report can be customized by parameters such as SOG, COG, draught etc. Individual vessels can be selected for detailed analysis.',
    type: ReportBackendTypes.TRACK,
    icon: reportTypeToIcon[ReportBackendTypes.TRACK],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
          allowFeatureCollections: true,
        },
      ],
    },
    miscellaneous: trackReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.TRIPS],
    type: ReportBackendTypes.TRIPS,
    description:
      'Get insights on trips between 2 areas done within a defined time. Includes visualization of trips and Individual vessels can be selected for detailed analysis.',
    icon: reportTypeToIcon[ReportBackendTypes.TRIPS],
    filter: {
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'departure',
          label: 'Departure area',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'SRCGAC',
          help: 'The area where the ship departs (must be an area with a port)',
        },
        {
          name: 'arrival',
          label: 'Arrival area',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'DESTGAC',
          help: 'The area where the ship arrives (must be an area with a port)',
        },
      ],
    },
    miscellaneous: tripsReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.MULTI_SHAPE],
    type: ReportBackendTypes.MULTI_SHAPE,
    description: 'Use multiple areas for ship analysis',
    icon: reportTypeToIcon[ReportBackendTypes.MULTI_SHAPE],
    filter: {
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      multiShape: [
        {
          name: 'shape',
          label: 'Shapes',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
          minItems: 2,
        },
      ],
    },
    miscellaneous: multiShapeReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.NEAR_MISS_SHIP_TO_POINT],
    type: ReportBackendTypes.NEAR_MISS_SHIP_TO_POINT,
    description:
      'Get map plots of vessels located near a defined point, based on a specified distance threshold. Individual vessels can be selected for detailed analysis.',
    icon: reportTypeToIcon[ReportBackendTypes.NEAR_MISS_SHIP_TO_POINT],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Point'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: shipToPointReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.NEAR_MISS_SHIP_TO_SHIP],
    type: ReportBackendTypes.NEAR_MISS_SHIP_TO_SHIP,
    description: 'Analyze near misses between ships',
    icon: reportTypeToIcon[ReportBackendTypes.NEAR_MISS_SHIP_TO_SHIP],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
      ship: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: shipToShipReportMiscellaneous,
    prefilledFieldsMap: shipToShipReportPrefill,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.PSS_DETAILED],
    type: ReportBackendTypes.PSS_DETAILED,
    description: 'Get detailed physical shore station information',
    icon: reportTypeToIcon[ReportBackendTypes.PSS_DETAILED],
    filter: null,
    shape: null,
    miscellaneous: {},
    disabled: true,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.PSS_OVERVIEW],
    type: ReportBackendTypes.PSS_OVERVIEW,
    description: 'Get a physical shore station overview',
    icon: reportTypeToIcon[ReportBackendTypes.PSS_OVERVIEW],
    filter: null,
    shape: null,
    miscellaneous: {},
    disabled: true,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.PASSAGELINE],
    type: ReportBackendTypes.PASSAGELINE,
    description:
      'Get vessel counts for crossings of a user-defined passage line, available as either a simple total or a histogram showing crossing density per line segment.',
    icon: reportTypeToIcon[ReportBackendTypes.PASSAGELINE],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['TwoPointLineString'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: passagelineReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.PASSAGELINE_SHIPS],
    type: ReportBackendTypes.PASSAGELINE_SHIPS,
    description: 'Get a passageline ships report for multiple lines',
    icon: reportTypeToIcon[ReportBackendTypes.PASSAGELINE_SHIPS],
    filter: {
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      multiShape: [
        {
          name: 'shape',
          label: 'Shapes',
          allowedGeometryTypes: ['TwoPointLineString'],
          backendKey: 'GAC',
          minItems: 2,
        },
      ],
    },
    miscellaneous: passagelineShipsReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.PILOT_OPERATIONS],
    type: ReportBackendTypes.PILOT_OPERATIONS,
    description: 'Get an overview of pilot operations',
    icon: reportTypeToIcon[ReportBackendTypes.PILOT_OPERATIONS],
    filter: null,
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: pilotReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.PORT_CALLS],
    type: ReportBackendTypes.PORT_CALLS,
    description:
      'Get vessel port calls for the selected port or port area, including actual time of arrival (ATA) and actual time of departure (ATD).',
    icon: reportTypeToIcon[ReportBackendTypes.PORT_CALLS],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
      ports: [
        {
          name: 'port',
          label: 'Ports',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'PORT',
        },
      ],
      worldShape: [
        {
          name: 'world',
          label: 'World',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'WS',
          help: 'Choose the world shape, when the report should run on the entire world. Use with caution!',
        },
      ],
    },
    miscellaneous: {},
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.REPLAY_VIDEO],
    type: ReportBackendTypes.REPLAY_VIDEO,
    description:
      'Create a replay video of a specific area and time of interest',
    icon: reportTypeToIcon[ReportBackendTypes.REPLAY_VIDEO],
    ships: {
      colorBy: true,
      filterBy: true,
    },
    filter: null,
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: replayVideoReportMiscellaneous,
    disabled: true,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.SOG_DISTRIBUTION],
    type: ReportBackendTypes.SOG_DISTRIBUTION,
    description:
      'Get accumulated SOG distribution (in knots) for a selected time interval (daily, weekly, yearly), including total distance traveled and time spent sailing at each speed range.',
    icon: reportTypeToIcon[ReportBackendTypes.SOG_DISTRIBUTION],
    filter: {
      ship: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: sogDistributionReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.SQL],
    type: ReportBackendTypes.SQL,
    description: 'Create your own SQL report to grab the data you want',
    icon: reportTypeToIcon[ReportBackendTypes.SQL],
    filter: null,
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon', 'Point', 'LineString', 'Circle'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: sqlReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.SHIPS],
    type: ReportBackendTypes.SHIPS,
    description:
      'Get tracks for one or more ships within a selected period, including map markers for key events such as signal loss/recovery, static data changes, voyage updates, and data source changes.',
    icon: reportTypeToIcon[ReportBackendTypes.SHIPS],
    filter: {
      ship: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: null,
    miscellaneous: {},
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.TRANSIT_PILOTAGE],
    type: ReportBackendTypes.TRANSIT_PILOTAGE,
    description: 'Get a transit pilotage report',
    icon: reportTypeToIcon[ReportBackendTypes.TRANSIT_PILOTAGE],
    filter: {
      combined: [
        {
          name: 'shiplist',
          label: 'Pilot shiplist',
          required: true,
          help: 'Select shiplist for pilot ships to include in the report',
        },
        {
          name: 'filter',
          label: 'Client filter',
          required: false,
          help: 'Choose filter matching ships receiving pilotage',
        },
      ],
    },
    shape: {
      multiShape: [
        {
          name: 'shape',
          label: 'Shapes',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
          help: 'Select from and to areas for pilotage report. Order is important, so shape 1 is from and shape 2 is to',
        },
      ],
    },
    miscellaneous: transitPilotageReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.TRANSPONDER_OFF],
    type: ReportBackendTypes.TRANSPONDER_OFF,
    description: 'Find out when ships have their transponder off',
    icon: reportTypeToIcon[ReportBackendTypes.TRANSPONDER_OFF],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
      shiplist: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: transponderOffReportMiscellaneous,
    disabled: false,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.TUGBOAT_OPERATIONS],
    type: ReportBackendTypes.TUGBOAT_OPERATIONS,
    description: 'Find out tugboat operations',
    icon: reportTypeToIcon[ReportBackendTypes.TUGBOAT_OPERATIONS],
    filter: {
      normal: [
        {
          name: 'filter',
          label: 'Filter',
          required: false,
        },
      ],
    },
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: {},
    disabled: true,
  },
  {
    label: reportTypeToLabel[ReportBackendTypes.WATCHDOG_EVENTS],
    type: ReportBackendTypes.WATCHDOG_EVENTS,
    description: 'Get an overview of triggered watchdog events',
    icon: reportTypeToIcon[ReportBackendTypes.WATCHDOG_EVENTS],
    filter: null,
    shape: {
      shape: [
        {
          name: 'shape',
          label: 'Shape',
          allowedGeometryTypes: ['Polygon'],
          backendKey: 'GAC',
        },
      ],
    },
    miscellaneous: watchdogReportMiscellaneous,
    disabled: false,
  },
];

export const reportTypesObject = reportTypes.reduce(
  (obj, item) => {
    obj[item.type] = item;
    return obj;
  },
  {} as { [key: string]: ReportType },
);
